import { ORDER_STATUS_LABELS, STOR_ZEBRA_EXTRAS, STOR_ZEBRA_LEGACY_EXTRAS } from '../constants';

// Date formatting - Firestore Timestamp ve Date nesnelerini destekler
export const formatDate = (dateValue) => {
  if (!dateValue) return '-';

  let date;

  // Firestore Timestamp nesnesi mi kontrol et
  if (dateValue && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }

  // Geçersiz tarih kontrolü
  if (isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('tr-TR');
};

// Tarih değerini input[type="date"] için YYYY-MM-DD formatına çevirir
export const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';

  let date;

  // Firestore Timestamp nesnesi mi kontrol et
  if (dateValue && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }

  // Geçersiz tarih kontrolü
  if (isNaN(date.getTime())) {
    return '';
  }

  // YYYY-MM-DD formatına çevir
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Price formatting - Türk para birimi formatı
// Aramalar için metni sadeleştirir: Türkçe küçük harf + ı/i farkı yok sayılır.
// Böylece "yita", "YİTA", "YITA" ve "Yita" birbirini bulur.
// (Standart toLowerCase "İ" harfini "i̇" yapar ve eşleşmez.)
export const normalizeSearchText = (text) => (
  String(text ?? '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .normalize('NFD')
    .replace(/̇/g, '') // olası birleşik nokta işaretini temizle
    .normalize('NFC')
    .trim()
);

export const formatPrice = (price) => {
  const num = parseFloat(price || 0);

  // Türk para birimi formatı: 1.234,56
  return num.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Motor seçim comboboxı: "Firma Adı - Motor" / "Firma Adı - Motor + Kumanda" string'ini ayrıştırır
export const parseMotorSelection = (motorType) => {
  if (!motorType) return { companyName: '', hasKumanda: false };
  const hasKumanda = motorType.endsWith('Motor + Kumanda');
  const companyName = motorType.replace(/ - Motor( \+ Kumanda)?$/, '').trim();
  return { companyName, hasKumanda };
};

// Firma listesinde isme göre motor firması ayarını bulur
export const findMotorCompany = (companies, companyName) => {
  if (!Array.isArray(companies) || !companyName) return null;
  return companies.find(c => (c.name || '').trim() === companyName.trim()) || null;
};

// Validation functions
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone) => {
  const phoneRegex = /^[0-9\s\-+()]{10,}$/;
  return phoneRegex.test(phone);
};

// Local storage helpers
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Local storage error:', error);
  }
};

export const getLocalStorage = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Local storage error:', error);
    return null;
  }
};

// Error handling
export const handleFirebaseError = (error) => {
  console.error('Firebase error:', error);

  switch (error.code) {
    case 'auth/user-not-found':
      return 'Bu email adresi ile kayıtlı kullanıcı bulunamadı.';
    case 'auth/wrong-password':
      return 'Şifre hatalı.';
    case 'auth/invalid-email':
      return 'Geçersiz email adresi.';
    case 'auth/too-many-requests':
      return 'Çok fazla başarısız giriş denemesi. Lütfen daha sonra tekrar deneyin.';
    case 'permission-denied':
      return 'Firebase erişim izni yok. Lütfen Firebase güvenlik kurallarını kontrol edin.';
    case 'unavailable':
      return 'Firebase servisi şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.';
    default:
      return 'Bir hata oluştu. Lütfen tekrar deneyin.';
  }
};

// Loading states
export const createLoadingState = () => ({
  isLoading: false,
  error: null,
  data: null
});

// Debounce function
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Tül Hesaplama Fonksiyonları
export const calculatePileDensity = (pileModel) => {
  const densityValues = {
    'Standart SEYREK': 2,
    'Standart ORTA': 2.5,
    'Standart SIK': 3,
    'S Pile': 3,
    'Amerikan Pile 2\'li': 3,
    'Amerikan Pile 3\'lü': 4,
    'Kapsül Pile': 3,
    'Çima Dikiş': 1,
    'Düz Dikiş': 1,
    'Kumaş Güneşlik': 1
  };

  return densityValues[pileModel] || 0;
};

// Kumaşın sabit yüksekliği (m) — "Özel Ölçü" hesabında kumaş 90° çevrilince bu, yatayda kaplanan pay olur
const FABRIC_HEIGHT_M = 3;
// Katlama payı (m) — hem normal hem Özel Ölçü hesabında bir kez eklenir
const FOLD_ALLOWANCE_M = 0.5;

export const calculateUsedM2 = (width, height, pileModel, customPileDensity = null, isOzelOlcu = false) => {
  // CM değerlerini metre'ye çevir (100'e böl)
  const widthNum = (parseFloat(width) || 0) / 100;
  const heightNum = (parseFloat(height) || 0) / 100;

  // Özel modeller (Pile sıklığı kullanılmayanlar) — Özel Ölçü hesabı bunlara uygulanmaz
  switch (pileModel) {
    case 'Çima Dikiş':
      return widthNum + 0.75;
    case 'Düz Dikiş':
      return widthNum + 0.5;
    case 'Kumaş Güneşlik':
      return widthNum + 0.2;
  }

  // Eğer custom pile sıklığı verilmişse onu kullan, yoksa pile modelinden hesapla
  let pileDensity = customPileDensity !== null ? parseFloat(customPileDensity) : calculatePileDensity(pileModel);

  if (!pileDensity || pileDensity === 0) {
    return widthNum;
  }

  // Özel Ölçü: tavan/boy yüksekliği kumaşın 3m'lik sabit yüksekliğini aştığında,
  // kumaş 90° çevrilerek kullanılır (3m'lik taraf yatayda, sınırsız taraf boyda kalır).
  if (isOzelOlcu) {
    const horizontalNeedWithFold = (widthNum * pileDensity) + FOLD_ALLOWANCE_M;
    const piecesNeeded = Math.ceil(horizontalNeedWithFold / FABRIC_HEIGHT_M);
    return piecesNeeded * heightNum;
  }

  // Pile sıklığı olan modeller için (normal hesap)
  return (widthNum * pileDensity) + FOLD_ALLOWANCE_M;
};

/*
 * Özel Proje (desenli kumaş) — yalnızca bilgi amaçlı, fiyat/maliyete etki etmez.
 * Kumaş eni boyunca paneller yan yana dikilir; her panel topdan sipariş boyu + boy payı
 * kadar kesilir ve rapora (desen tekrarı) göre yukarı yuvarlanır ki desenler yan yana devam etsin.
 * Kesim boyu raporun katı olduğundan, her topun başında yalnızca bir kez rapor hizalaması gerekir.
 */
export const calculateOzelProje = ({ width, height, pileModel, pileDensity, quantity, fabricWidthCm, rollLengthM, repeatCm, hemAllowanceCm }) => {
  const fabricWidth = parseFloat(fabricWidthCm) || 0;
  const heightCm = parseFloat(height) || 0;
  if (fabricWidth <= 0 || heightCm <= 0) return null;

  const density = parseFloat(pileDensity) > 0 ? parseFloat(pileDensity) : null;
  // Yatayda gereken kumaş (normal hesap — Özel Ölçü uygulanmaz)
  const horizontalNeedCm = calculateUsedM2(width, height, pileModel, density, false) * 100;
  if (horizontalNeedCm <= 0) return null;

  const repeat = parseFloat(repeatCm) > 0 ? parseFloat(repeatCm) : 0;
  const rollLengthCm = (parseFloat(rollLengthM) || 0) * 100;
  const qty = parseInt(quantity, 10) > 0 ? parseInt(quantity, 10) : 1;

  const panelsPerCurtain = Math.ceil(horizontalNeedCm / fabricWidth - 1e-9);
  const totalPanels = panelsPerCurtain * qty;
  const netDropCm = heightCm + (parseFloat(hemAllowanceCm) || 0);
  const cutLengthCm = repeat > 0 ? Math.ceil(netDropCm / repeat - 1e-9) * repeat : netDropCm;

  // Top kontrolü: top boyu girilmediyse tek parça kabul edilir
  let rollsNeeded = 1;
  let panelsPerRoll = totalPanels;
  let rollEndWasteCm = 0;
  let lastRollLeftoverCm = null;
  if (rollLengthCm > 0) {
    panelsPerRoll = Math.floor((rollLengthCm - repeat) / cutLengthCm);
    if (panelsPerRoll <= 0) {
      return { error: 'Panel kesim boyu top boyundan uzun', cutLengthCm };
    }
    rollsNeeded = Math.ceil(totalPanels / panelsPerRoll);
    const lastRollPanels = totalPanels - (rollsNeeded - 1) * panelsPerRoll;
    // Dolu toplarda sonda kalan, bir panele yetmeyen parça fire sayılır
    rollEndWasteCm = (rollsNeeded - 1) * (rollLengthCm - repeat - panelsPerRoll * cutLengthCm);
    lastRollLeftoverCm = rollLengthCm - repeat - lastRollPanels * cutLengthCm;
  }

  const alignmentWasteCm = rollsNeeded * repeat;
  const repeatWasteCm = totalPanels * (cutLengthCm - netDropCm);
  const netFabricCm = totalPanels * netDropCm;
  const totalFabricCm = netFabricCm + repeatWasteCm + alignmentWasteCm + rollEndWasteCm;

  return {
    horizontalNeedM: horizontalNeedCm / 100,
    panelsPerCurtain,
    totalPanels,
    netDropM: netDropCm / 100,
    cutLengthM: cutLengthCm / 100,
    rollsNeeded,
    panelsPerRoll,
    widthWasteCm: panelsPerCurtain * fabricWidth - horizontalNeedCm,
    repeatWasteM: repeatWasteCm / 100,
    alignmentWasteM: alignmentWasteCm / 100,
    rollEndWasteM: rollEndWasteCm / 100,
    totalWasteM: (repeatWasteCm + alignmentWasteCm + rollEndWasteCm) / 100,
    netFabricM: netFabricCm / 100,
    totalFabricM: totalFabricCm / 100,
    lastRollLeftoverM: lastRollLeftoverCm !== null ? lastRollLeftoverCm / 100 : null
  };
};

export const calculateCapsulePrice = (usedM2, capsuleUnitPrice) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const capsuleUnitPriceNum = parseFloat(capsuleUnitPrice) || 0;

  // Metrekareyi bir üst tam sayıya yuvarla
  const roundedM2 = Math.ceil(usedM2Num);

  // Metrekarede 7 kapsül kullanılıyor
  const capsuleCount = roundedM2 * 7;

  return capsuleCount * capsuleUnitPriceNum;
};

export const calculateCapsuleCost = (usedM2, capsuleUnitCost) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const capsuleUnitCostNum = parseFloat(capsuleUnitCost) || 0;

  // Metrekareyi bir üst tam sayıya yuvarla
  const roundedM2 = Math.ceil(usedM2Num);

  // Metrekarede 7 kapsül kullanılıyor
  const capsuleCount = roundedM2 * 7;

  return capsuleCount * capsuleUnitCostNum;
};

export const calculateTotalPrice = (usedM2, unitPrice, extraPrice, pileModel, capsulePrice = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const extraPriceNum = parseFloat(extraPrice) || 0;
  const capsulePriceNum = parseFloat(capsulePrice) || 0;

  const basePrice = usedM2Num * (unitPriceNum + extraPriceNum);
  
  if (pileModel === 'Kapsül Pile') {
    return basePrice + capsulePriceNum;
  }

  return basePrice;
};

export const calculateTotalCost = (usedM2, unitCost, extraCost = 0, pileModel = '', capsuleCost = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCostNum = parseFloat(unitCost) || 0;
  const extraCostNum = parseFloat(extraCost) || 0;
  const capsuleCostNum = parseFloat(capsuleCost) || 0;

  const baseCost = usedM2Num * (unitCostNum + extraCostNum);

  if (pileModel === 'Kapsül Pile') {
    return baseCost + capsuleCostNum;
  }

  return baseCost;
};

/** Kanat astar ücreti: kullanılan mt × birim fiyat × adet */
export const calculateAstarPrice = (usedM, astarUnitPrice, quantity = 1) => {
  const used = parseFloat(usedM) || 0;
  const unit = parseFloat(astarUnitPrice) || 0;
  const qty = parseFloat(quantity) || 1;
  return used * unit * qty;
};

/** Kanat astar maliyeti: kullanılan mt × birim maliyet × adet */
export const calculateAstarCost = (usedM, astarUnitCost, quantity = 1) => {
  const used = parseFloat(usedM) || 0;
  const unit = parseFloat(astarUnitCost) || 0;
  const qty = parseFloat(quantity) || 1;
  return used * unit * qty;
};

export const calculateDiscountedPrice = (totalPrice, discount) => {
  const totalPriceNum = parseFloat(totalPrice) || 0;
  const discountNum = parseFloat(discount) || 0;

  if (discountNum <= 0) return totalPriceNum;

  const discountAmount = totalPriceNum * (discountNum / 100);
  return totalPriceNum - discountAmount;
};

// Stor ve Zebra Hesaplama Fonksiyonları
export const calculateStorZebraUsedM2 = (width, height) => {
  const widthCm = parseFloat(width) || 0;
  const heightCm = parseFloat(height) || 0;

  // En minimum 1 m, boy minimum 2 m; aşan ölçü bir üst ondalığa (10 cm) tamamlanır.
  // Hesap santimetre üzerinden tam sayıyla yapılır (1.1 * 10 = 11.000000000000002 gibi kayan nokta hataları olmasın).
  const widthDm = Math.max(10, Math.ceil(widthCm / 10)); // desimetre
  const heightDm = Math.max(20, Math.ceil(heightCm / 10));

  return (widthDm * heightDm) / 100;
};

// Stor/Zebra ek seçenekleri normalde (50 cm'ye yuvarlanmış en × fiyat) olarak eklenir.
// Buradaki anahtarlar en ile çarpılmadan, sabit tutar olarak eklenir ('storJumbo'/'zebraJumbo' eski çağrılar için).
const STOR_ZEBRA_FIXED_EXTRAS = [
  'storJumbo', 'zebraJumbo',
  ...new Set(Object.values(STOR_ZEBRA_EXTRAS).flat().filter(e => e.fixed).map(e => e.key))
];

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Bir ek seçeneğin sipariş ve eski (tek firmalı) ayar alan adları: ('stor', 'jumbo') → hasStorJumbo, storJumboAmount...
export const getStorZebraExtraFields = (type, key) => {
  const base = `${type}${capitalize(key)}`;
  return {
    has: `has${capitalize(base)}`,
    amount: `${base}Amount`,
    costAmount: `${base}CostAmount`,
    variant: `${base}Variant`,
    variants: `${base}Variants`,
    legacyPrice: `${base}Price`,
    legacyCost: `${base}Cost`
  };
};

// Seçeneğin fiyatlandırılan çeşitleri (çeşidi yoksa kendisi tek seçenektir)
export const getStorZebraExtraOptions = (extra) => extra.variants || [{ key: extra.key, label: extra.label }];

// Siparişte seçili çeşit (kayıt yoksa ilk çeşit — eski siparişler varsayılan çeşitle açılır)
export const getStorZebraSelectedOption = (extra, order, type) => {
  const options = getStorZebraExtraOptions(extra);
  const variantKey = order[getStorZebraExtraFields(type, extra.key).variant];
  return options.find(o => o.key === variantKey) || options[0];
};

// Çoklu seçimli seçenekte seçili çeşitler (tek seçimle kaydedilmiş eski siparişler tek elemanlı liste olur)
export const getStorZebraSelectedOptions = (extra, order, type) => {
  if (!extra.multiple) return [getStorZebraSelectedOption(extra, order, type)];
  const f = getStorZebraExtraFields(type, extra.key);
  const keys = Array.isArray(order[f.variants]) ? order[f.variants] : (order[f.variant] ? [order[f.variant]] : []);
  return getStorZebraExtraOptions(extra).filter(o => keys.includes(o.key));
};

// Seçilebilir + eski (yalnızca eski siparişlerde görünen) seçenekler
const allStorZebraExtras = (type) => [...(STOR_ZEBRA_EXTRAS[type] || []), ...(STOR_ZEBRA_LEGACY_EXTRAS[type] || [])];

const forEachStorZebraExtra = (fn) => Object.keys(STOR_ZEBRA_EXTRAS)
  .flatMap(type => allStorZebraExtras(type).map(extra => fn(getStorZebraExtraFields(type, extra.key), type, extra)));

// Hesaplamayı tetikleyen tüm Stor/Zebra ek seçenek alanları
export const STOR_ZEBRA_EXTRA_PRICE_FIELDS = forEachStorZebraExtra((f) => [f.has, f.amount, f.variant, f.variants]).flat();
export const STOR_ZEBRA_EXTRA_COST_FIELDS = forEachStorZebraExtra((f) => [f.has, f.costAmount, f.variant, f.variants]).flat();

// Yeni/boş sipariş için Stor/Zebra ek seçenek alanları (seçili firma dahil)
export const emptyStorZebraExtraOrderFields = () => Object.fromEntries([
  ['storZebraExtraFirmaId', ''],
  ['storZebraExtraFirmaName', ''],
  ...forEachStorZebraExtra((f, type, extra) => [
    [f.has, false], [f.amount, 0], [f.costAmount, 0],
    ...(extra.variants ? [[f.variant, '']] : []),
    ...(extra.multiple ? [[f.variants, []]] : [])
  ]).flat()
]);

// Düzenlemeye yüklenen siparişten Stor/Zebra ek seçenek alanları
export const pickStorZebraExtraOrderFields = (order) => Object.fromEntries([
  ['storZebraExtraFirmaId', order.storZebraExtraFirmaId || ''],
  ['storZebraExtraFirmaName', order.storZebraExtraFirmaName || ''],
  ...forEachStorZebraExtra((f, type, extra) => [
    [f.has, order[f.has] || false], [f.amount, order[f.amount] || 0], [f.costAmount, order[f.costAmount] || 0],
    ...(extra.variants ? [[f.variant, order[f.variant] || '']] : []),
    ...(extra.multiple ? [[f.variants, getStorZebraSelectedOptions(extra, order, type).map(o => o.key)]] : [])
  ]).flat()
]);

// calculateStorZebraTotalPrice/Cost'a verilecek ek tutarlar (mode: 'price' | 'cost')
export const buildStorZebraExtras = (order, type, mode = 'price') => Object.fromEntries(
  allStorZebraExtras(type).map(({ key }) => {
    const f = getStorZebraExtraFields(type, key);
    return [key, order[f.has] ? order[mode === 'cost' ? f.costAmount : f.amount] : 0];
  })
);

export const hasAnyStorZebraExtra = (order, type) =>
  allStorZebraExtras(type).some(({ key }) => order[getStorZebraExtraFields(type, key).has]);

// Siparişte işaretli seçeneklerin okunur listesi: [{ label, value }] (value: seçilen çeşit, çeşitsizse null)
export const describeStorZebraExtras = (order, type) => allStorZebraExtras(type)
  .filter(({ key }) => order[getStorZebraExtraFields(type, key).has])
  .map((extra) => {
    if (!extra.variants) return { label: extra.label, value: null };
    if (extra.multiple) {
      const labels = getStorZebraSelectedOptions(extra, order, type).map(o => o.label);
      return { label: extra.label, value: labels.length ? labels.join(', ') : null };
    }
    const option = getStorZebraSelectedOption(extra, order, type);
    return { label: extra.label, value: option.label === extra.label ? null : option.label };
  });

// Ayarlardaki ek ücret firmaları: prices[çeşitKey] = { price, cost }.
// Kayıtlı firma yoksa eski tekil fiyatlar "Firma 1" olarak gelir.
export const normalizeStorZebraExtraCompanies = (type, data = {}) => {
  const optionKeys = (STOR_ZEBRA_EXTRAS[type] || []).flatMap(extra => getStorZebraExtraOptions(extra).map(o => o.key));
  const saved = data[`${type}ExtraCompanies`];
  if (Array.isArray(saved) && saved.length > 0) {
    return saved.map((company, i) => ({
      id: company.id || `firma-${i + 1}`,
      name: company.name || `Firma ${i + 1}`,
      prices: Object.fromEntries(optionKeys.map(key => [key, {
        price: company.prices?.[key]?.price ?? '',
        cost: company.prices?.[key]?.cost ?? ''
      }]))
    }));
  }
  return [{
    id: 'default',
    name: 'Firma 1',
    prices: Object.fromEntries(optionKeys.map(key => {
      const f = getStorZebraExtraFields(type, key);
      return [key, { price: data[f.legacyPrice] ?? '', cost: data[f.legacyCost] ?? '' }];
    }))
  }];
};

const sumStorZebraExtras = (extras, widthForExtras) =>
  Object.entries(extras).reduce((sum, [key, val]) => {
    const valNum = parseFloat(val) || 0;
    return sum + (STOR_ZEBRA_FIXED_EXTRAS.includes(key) ? valNum : valNum * widthForExtras);
  }, 0);

export const calculateStorZebraTotalPrice = (usedM2, unitPrice, width, extras = {}, motorPriceTL = 0, mounting = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;

  // Ekstra seçenekler için özel en hesaplaması
  let widthForExtras = (parseFloat(width) || 0) / 100; // cm -> metre
  if (widthForExtras > 0) {
    if (widthForExtras < 1) widthForExtras = 1;
    // 50cm (0.5m) katlarına yukarı yuvarla
    widthForExtras = Math.ceil(widthForExtras * 2) / 2;
  }

  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;
  const mountingNum = parseFloat(mounting) || 0;

  const basePrice = usedM2Num * unitPriceNum;
  
  // Ekstra ücretleri hesaplanan en (widthForExtras) ile çarpıyoruz (Stor/Zebra jumbo hariç: sabit)
  const extraCostsTotal = sumStorZebraExtras(extras, widthForExtras);

  return basePrice + extraCostsTotal + motorPriceTLNum + mountingNum;
};

export const calculateStorZebraTotalCost = (usedM2, unitCost, width, extras = {}, motorCostTL = 0, mountingCost = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCostNum = parseFloat(unitCost) || 0;
  const mountingCostNum = parseFloat(mountingCost) || 0;
  
  let widthForExtras = (parseFloat(width) || 0) / 100;
  if (widthForExtras > 0) {
    if (widthForExtras < 1) widthForExtras = 1;
    widthForExtras = Math.ceil(widthForExtras * 2) / 2;
  }

  const motorCostTLNum = parseFloat(motorCostTL) || 0;
  const baseCost = usedM2Num * unitCostNum;
  
  const extraCostsTotal = sumStorZebraExtras(extras, widthForExtras);

  return baseCost + extraCostsTotal + motorCostTLNum + mountingCostNum;
};

// Ahşap Jaluzi ve Metal Jaluzi Hesaplama Fonksiyonları
export const calculateJaluziUsedM2 = (width, height) => {
  // CM değerlerini metre'ye çevir (100'e böl)
  let widthNum = (parseFloat(width) || 0) / 100;
  let heightNum = (parseFloat(height) || 0) / 100;

  // En için minimum 1 metre kontrolü
  if (widthNum < 1) {
    widthNum = 1;
  }

  // Boy için minimum 1 metre kontrolü
  if (heightNum < 1) {
    heightNum = 1;
  }

  // 10'un katına yuvarlama
  widthNum = Math.ceil(widthNum * 10) / 10;
  heightNum = Math.ceil(heightNum * 10) / 10;

  return widthNum * heightNum;
};

export const calculateJaluziTotalPrice = (usedM2, unitPrice, mounting = 0, motorPrice = 0, zigilAmount = 0, ikiCamArasiAmount = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceNum = parseFloat(motorPrice) || 0;
  const zigilAmountNum = parseFloat(zigilAmount) || 0;
  const ikiCamArasiAmountNum = parseFloat(ikiCamArasiAmount) || 0;

  const basePrice = usedM2Num * unitPriceNum;

  return basePrice + mountingNum + motorPriceNum + zigilAmountNum + ikiCamArasiAmountNum;
};

export const calculateJaluziTotalCost = (usedM2, unitCost, motorCostTL = 0, zigilAmountCost = 0, ikiCamArasiAmountCost = 0, mountingCost = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCostNum = parseFloat(unitCost) || 0;
  const motorCostTLNum = parseFloat(motorCostTL) || 0;
  const zigilAmountCostNum = parseFloat(zigilAmountCost) || 0;
  const ikiCamArasiAmountCostNum = parseFloat(ikiCamArasiAmountCost) || 0;
  const mountingCostNum = parseFloat(mountingCost) || 0;

  const baseCost = usedM2Num * unitCostNum;

  return baseCost + motorCostTLNum + zigilAmountCostNum + ikiCamArasiAmountCostNum + mountingCostNum;
};

// Dikey Perde Hesaplama Fonksiyonu
export const calculateDikeyPerdeTotalPrice = (usedM2, unitPrice, mounting = 0, motorPriceTL = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;

  const basePrice = usedM2Num * unitPriceNum;

  return basePrice + mountingNum + motorPriceTLNum;
};

export const calculateDikeyPerdeTotalCost = (usedM2, unitCost, motorCostTL = 0, mountingCost = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCostNum = parseFloat(unitCost) || 0;
  const motorCostTLNum = parseFloat(motorCostTL) || 0;
  const mountingCostNum = parseFloat(mountingCost) || 0;

  const baseCost = usedM2Num * unitCostNum;

  return baseCost + motorCostTLNum + mountingCostNum;
};

// Plise Hesaplama Fonksiyonu
export const calculatePliseUsedM2 = (width, height) => {
  // CM değerlerini metre'ye çevir (100'e böl)
  let widthNum = (parseFloat(width) || 0) / 100;
  let heightNum = (parseFloat(height) || 0) / 100;

  // 10'un katına yuvarlama
  widthNum = Math.ceil(widthNum * 10) / 10;
  heightNum = Math.ceil(heightNum * 10) / 10;

  // İlk çarpım hesaplama
  const calculatedArea = widthNum * heightNum;

  // Eğer çarpım 1'den büyükse, değerleri aynen kullan
  // Eğer çarpım 1'den küçükse, direkt 1 olarak ayarla
  if (calculatedArea >= 1) {
    return calculatedArea;
  } else {
    return 1;
  }
};

export const calculatePliseTotalPrice = (usedM2, unitPrice1 = 0, unitPrice2 = 0, mounting = 0, extraPrice = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPrice1Num = parseFloat(unitPrice1) || 0;
  const unitPrice2Num = parseFloat(unitPrice2) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const extraPriceNum = parseFloat(extraPrice) || 0;

  // Toplam birim fiyat (iki ürünün toplamı)
  const totalUnitPrice = unitPrice1Num + unitPrice2Num;

  // Eğer m2 0 ise (ölçü girilmemişse) varsayılan 1 m2 üzerinden hesapla 
  const effectiveM2 = usedM2Num > 0 ? usedM2Num : 1;

  const basePrice = effectiveM2 * totalUnitPrice;

  return basePrice + mountingNum + extraPriceNum;
};

export const calculatePliseTotalCost = (usedM2, unitCost1 = 0, unitCost2 = 0, mountingCost = 0, manualExtraCost = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCost1Num = parseFloat(unitCost1) || 0;
  const unitCost2Num = parseFloat(unitCost2) || 0;
  const mountingCostNum = parseFloat(mountingCost) || 0;
  const manualExtraCostNum = parseFloat(manualExtraCost) || 0;

  const totalUnitCost = unitCost1Num + unitCost2Num;
  const effectiveM2 = usedM2Num > 0 ? usedM2Num : 1;

  const baseCost = effectiveM2 * totalUnitCost;

  return baseCost + mountingCostNum + manualExtraCostNum;
};

// Aksesuar Hesaplama Fonksiyonu
export const calculateAksesuarTotalPrice = (unitPrice, quantity, usedMeter = 0, birimTipi = 'adet') => {
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const quantityNum = parseFloat(quantity) || 0;

  if (birimTipi === 'metre') {
    const meterNum = parseFloat(usedMeter) || 0;
    return unitPriceNum * meterNum * quantityNum;
  }

  return unitPriceNum * quantityNum;
};

export const calculateAksesuarTotalCost = (unitCost, quantity, mountingCost = 0, usedMeter = 0, birimTipi = 'adet') => {
  const uCostNum = parseFloat(unitCost) || 0;
  const qtyNum = parseFloat(quantity) || 0;
  const mountNum = parseFloat(mountingCost) || 0;

  if (birimTipi === 'metre') {
    const meterNum = parseFloat(usedMeter) || 0;
    return (uCostNum * meterNum * qtyNum) + mountNum;
  }

  return (uCostNum * qtyNum) + mountNum;
};

// Katlamalı Perde Hesaplama Fonksiyonları
export const calculateKatlamaliPerdeUsedM = (width) => {
  // CM değerini metre'ye çevir ve 0.35 ekle
  const widthNum = (parseFloat(width) || 0) / 100;
  return widthNum + 0.35;
};

export const calculateKatlamaliPerdeTotalPrice = (usedM, fabricPrice = 0, mechanismPrice = 0, accessories = [], mounting = 0, motorPrice = 0) => {
  const usedMNum = parseFloat(usedM) || 0;
  const fabricPriceNum = parseFloat(fabricPrice) || 0;
  const mechanismPriceNum = parseFloat(mechanismPrice) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceNum = parseFloat(motorPrice) || 0;

  // Aksesuar toplamını hesapla (dizi ise)
  let totalAccessoryPrice = 0;
  if (Array.isArray(accessories)) {
    totalAccessoryPrice = accessories.reduce((sum, acc) => sum + (parseFloat(acc.total) || 0), 0);
  } else {
    totalAccessoryPrice = parseFloat(accessories) || 0;
  }

  // Toplam Tutar = (Kullanılan metre x (Kumaş + Mekanizma)) + Aksesuarlar + Montaj + Motor
  const basePrice = usedMNum * (fabricPriceNum + mechanismPriceNum);

  return basePrice + totalAccessoryPrice + mountingNum + motorPriceNum;
};

export const calculateKatlamaliPerdeTotalCost = (usedM, fabricCost = 0, mechanismCost = 0, accessories = [], motorCost = 0, mountingCost = 0) => {
  const usedMNum = parseFloat(usedM) || 0;
  const fabricCostNum = parseFloat(fabricCost) || 0;
  const mechanismCostNum = parseFloat(mechanismCost) || 0;
  const motorCostNum = parseFloat(motorCost) || 0;
  const mountingCostNum = parseFloat(mountingCost) || 0;

  // Aksesuar toplamını hesapla (dizi ise)
  let totalAccessoryCost = 0;
  if (Array.isArray(accessories)) {
    totalAccessoryCost = accessories.reduce((sum, acc) => {
      const c = parseFloat(acc.cost) || 0;
      const q = parseFloat(acc.quantity) || 1;
      return sum + (c * q);
    }, 0);
  } else {
    totalAccessoryCost = parseFloat(accessories) || 0;
  }

  // Toplam Maliyet = (Kullanılan metre x (Kumaş + Mekanizma)) + Aksesuarlar + Motor
  const baseCost = usedMNum * (fabricCostNum + mechanismCostNum);

  return baseCost + totalAccessoryCost + motorCostNum + mountingCostNum;
};

// Rustik Hesaplama Fonksiyonu
export const calculateRustikTotalCost = (
  unitCost,
  ekAyakSayisi,
  ayakUcretiCost,
  mountingCost,
  usedCm,
  rustikRaySecenekleri = [],
  rustikAksesuarlar = []
) => {
  const uCost = parseFloat(unitCost) || 0;
  const ekAyakS = parseFloat(ekAyakSayisi) || 0;
  const ayakUCost = parseFloat(ayakUcretiCost) || 0;
  const mountCost = parseFloat(mountingCost) || 0;

  // Aksesuar ve Ray seçenekleri maliyet toplamı
  const accCostTotal = rustikAksesuarlar.reduce((sum, item) => sum + (parseFloat(item.cost) || 0) * (parseInt(item.quantity) || 1), 0);
  const usedCmNum = parseFloat(usedCm) || 0;
  const rayCostTotal = rustikRaySecenekleri.reduce((sum, item) => sum + (parseFloat(item.cost) || 0) * (usedCmNum / 100), 0);

  return uCost + (ekAyakS * ayakUCost) + accCostTotal + rayCostTotal + mountCost;
};

export const getOrderQuantity = (order) => {
  if (!order) return 0;
  
  // Plise siparişleri için tüm ölçülerin adetlerini topla
  if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
    return order.pliseItems.reduce((sum, item) => {
      const q = parseFloat(item.quantity);
      return sum + (isNaN(q) ? 1 : q);
    }, 0);
  }
  
  // Diğer ürün tipleri için ana adet alanını kullan
  const qty = parseFloat(order.quantity);
  return isNaN(qty) ? 1 : qty;
};

// Dashboard İstatistik Hesaplama Fonksiyonları
export const calculateOrderStats = (orders) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const stats = {
    total: orders.reduce((sum, order) => sum + getOrderQuantity(order), 0),
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  };

  orders.forEach(order => {
    // Sipariş tarihi varsa onu kullan, yoksa createdAt kullan
    const dateToUse = order.orderDate || order.createdAt;
    const createdAt = dateToUse?.toDate() || new Date(dateToUse);

    const qty = getOrderQuantity(order);

    if (createdAt >= today) {
      stats.today += qty;
    }
    if (createdAt >= thisWeek) {
      stats.thisWeek += qty;
    }
    if (createdAt >= thisMonth) {
      stats.thisMonth += qty;
    }
  });

  return stats;
};

export const calculateRevenueStats = (orders) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const stats = {
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    average: 0
  };

  orders.forEach(order => {
    // İndirimli tutar varsa onu kullan, yoksa toplam tutarı kullan
    const orderAmount = order.discountedPrice && parseFloat(order.discount) > 0
      ? parseFloat(order.discountedPrice || 0)
      : parseFloat(order.totalPrice || 0);

    stats.total += orderAmount;

    // Siparişler için createdAt kullan
    const createdAt = order.createdAt?.toDate() || new Date(order.createdAt);

    if (createdAt >= today) {
      stats.today += orderAmount;
    }
    if (createdAt >= thisWeek) {
      stats.thisWeek += orderAmount;
    }
    if (createdAt >= thisMonth) {
      stats.thisMonth += orderAmount;
    }
  });

  stats.average = orders.length > 0 ? stats.total / orders.length : 0;

  return stats;
};

export const calculateProductTypeDistribution = (orders) => {
  const distribution = {};

  orders.forEach(order => {
    const productType = order.productType || 'unknown';
    distribution[productType] = (distribution[productType] || 0) + 1;
  });

  return distribution;
};

export const calculateOrderStatusDistribution = (customers) => {
  const distribution = {};

  // ORDER_STATUS_LABELS'dan tüm durumları al
  Object.values(ORDER_STATUS_LABELS).forEach(label => {
    distribution[label] = 0;
  });

  customers.forEach(customer => {
    const status = customer.orderStatus || 'new_order';
    const statusLabel = ORDER_STATUS_LABELS[status] || 'Yeni Sipariş';
    distribution[statusLabel] = (distribution[statusLabel] || 0) + 1;
  });

  return distribution;
};

export const calculateMonthlyCustomerGrowth = (customers) => {
  const monthlyData = {};

  customers.forEach(customer => {
    // Sipariş tarihi varsa onu kullan, yoksa createdAt kullan
    const dateToUse = customer.orderDate || customer.createdAt;
    const createdAt = dateToUse?.toDate() || new Date(dateToUse);
    const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;

    monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
  });

  return monthlyData;
};

export const calculateMonthlyRevenue = (orders) => {
  const monthlyData = {};

  orders.forEach(order => {
    // Siparişler için createdAt kullan
    const createdAt = order.createdAt?.toDate() || new Date(order.createdAt);
    const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;

    const orderAmount = order.discountedPrice && parseFloat(order.discount) > 0
      ? parseFloat(order.discountedPrice || 0)
      : parseFloat(order.totalPrice || 0);

    monthlyData[monthKey] = (monthlyData[monthKey] || 0) + orderAmount;
  });

  return monthlyData;
}; 